import { useEffect, useRef, useState } from 'react';
import type { CandidateResponseRow } from '../../db/types';
import { partyVar } from '../../lib/buildCandidates';
import './KnowBeforeYouVote.css';

const ROTATE_INTERVAL_MS = 5000;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function KBYVCarousel() {
  const [profiles, setProfiles] = useState<CandidateResponseRow[]>([]);
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetch('/api/know-before-you-vote', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: { profiles: CandidateResponseRow[] }) => setProfiles(d.profiles))
      .catch(() => { /* silent — the block just won't appear */ });
  }, []);

  useEffect(() => {
    if (profiles.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % profiles.length);
    }, ROTATE_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [profiles.length]);

  function goTo(i: number) {
    setIndex(i);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex((cur) => (cur + 1) % profiles.length);
    }, ROTATE_INTERVAL_MS);
  }

  if (profiles.length === 0) return null;

  const profile = profiles[index];
  const color = partyVar(profile.party);
  const snippet = profile.why_vote ?? profile.top_priorities ?? profile.malta_vision ?? '';

  return (
    <section className="kbyv-carousel" aria-label="Know Before You Vote">
      <header className="kbyv-carousel__header">
        <div>
          <p className="kbyv-carousel__kicker">Candidate spotlight</p>
          <h2 className="kbyv-carousel__title">
            <a href="/know-before-you-vote">Know Before You Vote</a>
          </h2>
        </div>
        <span className="kbyv-carousel__sponsored" aria-label="Sponsored section">
          Sponsored
        </span>
      </header>

      <a
        className="kbyv-carousel__card"
        href={`/know-before-you-vote/${profile.slug}`}
        style={{ borderLeftColor: color }}
        aria-label={`Read ${profile.candidate_name}'s profile`}
      >
        <div className="kbyv-carousel__inner">
          <div
            className="kbyv-avatar kbyv-carousel__avatar"
            style={{ background: color }}
            aria-hidden="true"
          >
            {initials(profile.candidate_name)}
          </div>
          <div className="kbyv-carousel__body">
            <p className="kbyv-carousel__name">{profile.candidate_name}</p>
            <div className="kbyv-carousel__meta">
              <span
                className="kbyv-carousel__party"
                style={{ background: color }}
              >
                {profile.party}
              </span>
              {profile.district && (
                <span className="kbyv-carousel__district">
                  District {profile.district}
                </span>
              )}
            </div>
            {snippet && <p className="kbyv-carousel__quote">{snippet}</p>}
          </div>
        </div>
      </a>

      {profiles.length > 1 && (
        <div className="kbyv-carousel__dots" role="tablist" aria-label="Select candidate">
          {profiles.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Candidate ${i + 1}`}
              className={`kbyv-carousel__dot${i === index ? ' is-active' : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}

      <a className="kbyv-carousel__cta" href="/know-before-you-vote">
        View all candidates →
      </a>
    </section>
  );
}
